import React, { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { VideoIdSigningOtpMethod } from '../../../constants/videoIdValues'
import { ISigningRequest } from '../../../interfaces/i-signing-request'
import { ButtonSpinner } from '../Spinners'
import { CountdownTimer } from '../../../utils/dateAndTimeHelper'

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

interface Props {
	timedOut: boolean
	onTimeout: (bool: boolean) => void
	otpCode: string
	setOtpCode: (code: string) => void
	loading: boolean
	selectedOtpMethod: VideoIdSigningOtpMethod
	signingRequest: ISigningRequest | undefined
	onValidate: (otp: string) => void
	onBack: () => void
}

export const VideoIdOtpValidation = ({
	timedOut,
	onTimeout,
	otpCode,
	setOtpCode,
	loading,
	selectedOtpMethod,
	signingRequest,
	onValidate,
	onBack,
}: Props) => {
	const { t } = useTranslation()
	const otpInputRef = useRef<HTMLInputElement>(null)

	// iOS workaround: onChange doesn't reliably fire for programmatic input on iOS
	useEffect(() => {
		if (!isIOS || !otpInputRef.current) return
		const input = otpInputRef.current
		const updateValue = (e: Event) => setOtpCode((e.target as HTMLInputElement).value)
		input.addEventListener('input', updateValue)
		return () => input.removeEventListener('input', updateValue)
	}, [])  // eslint-disable-line react-hooks/exhaustive-deps

	const destination =
		selectedOtpMethod === VideoIdSigningOtpMethod.SMS
			? signingRequest?.phoneNumber ?? ''
			: signingRequest?.email ?? ''

	return (
		<div className="sign-pn-otp-validate">
			<div className="otp-section-sms-and-email">
				<div className="otp-information-top">
					<Trans t={t} i18nKey="OtpValidateText">
						Code has been sent to
						<strong>{{ state: destination }}</strong>
						and is valid for another:
					</Trans>
				</div>
				<div className={`otp-timer ${timedOut ? 'time-out' : ''}`}>
					{timedOut ? (
						t('OtpTimeOut')
					) : (
						<CountdownTimer
							isTimedOut={onTimeout}
							format={`mm:ss ${t('Minutes')}`}
						/>
					)}
				</div>
				{!timedOut && (
					<>
						<div className="otp-information-bottom">
							<Trans t={t} i18nKey="OtpValidateText2">
								<strong>The code takes a few seconds to arrive.</strong>{' '}
								If at the end of the time you have not received any message,
								please click back to go back one step and request the code again
								by choosing another shipping method.
							</Trans>
						</div>
						<div className="otp-code-entry-section">
							<input
								ref={otpInputRef}
								className="form-control"
								id="sign-promissory-note-otp-validate-input-field"
								type="text"
								placeholder={t('OTPCode')}
								value={otpCode}
								disabled={loading}
								onChange={(e) => setOtpCode(e.target.value)}
							/>
							<button
								disabled={!otpCode}
								type="button"
								id="sign-promissory-note-otp-validate-button"
								className="btn btn-primary validate-btn"
								onClick={() => setTimeout(() => onValidate(otpCode), 400)}>
								{loading ? (
									<div className="pn-spinner-container">
										<ButtonSpinner />
									</div>
								) : (
									<div className="file-name">{t('ProceedValidateOtpCode')}</div>
								)}
							</button>
						</div>
						<div className="otp-information-bottom field-note">
							{t('OtpValidateText3')}
						</div>
					</>
				)}
				<div className="back-section">
					<button
						type="button"
						id="sign-promissory-note-back-button"
						className="btn btn-secondary dark-gray back-btn"
						onClick={onBack}>
						{timedOut ? t('Retry') : t('Back')}
					</button>
				</div>
			</div>
		</div>
	)
}
